import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useSession } from './auth';

export type ChildProfile = {
  id: string;
  display_name: string;
  member_color: string;
  avatar_url: string | null;
  created_at: string;
  guardians: Array<{ id: string; display_name: string }>;
};

export function useFamilyChildren() {
  const { profile } = useSession();
  return useQuery<ChildProfile[]>({
    queryKey: ['family-children', profile?.family_id],
    enabled: !!profile?.family_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(
          `id, display_name, member_color, avatar_url, created_at,
           child_guardians!child_guardians_child_id_fkey(
             guardian:profiles!child_guardians_guardian_id_fkey(id, display_name)
           )`
        )
        .eq('family_id', profile!.family_id!)
        .eq('is_child', true)
        .order('created_at', { ascending: true });
      if (error) throw error;
      // PostgREST embeds always come back as arrays even for 1:1 joins,
      // so unwrap defensively for both shapes.
      type Guardian = { id: string; display_name: string };
      type GuardianEmbed = Guardian | Guardian[] | null;
      type Row = {
        id: string;
        display_name: string;
        member_color: string;
        avatar_url: string | null;
        created_at: string;
        child_guardians?: Array<{ guardian: GuardianEmbed }>;
      };
      return (data as unknown as Row[] ?? []).map((r) => ({
        id: r.id,
        display_name: r.display_name,
        member_color: r.member_color,
        avatar_url: r.avatar_url,
        created_at: r.created_at,
        guardians: (r.child_guardians ?? [])
          .flatMap((cg): Guardian[] => {
            if (!cg.guardian) return [];
            return Array.isArray(cg.guardian) ? cg.guardian : [cg.guardian];
          }),
      }));
    },
  });
}

export function useCreateChild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { displayName: string; memberColor: string; avatarUrl?: string | null }) => {
      const { data, error } = await supabase.rpc('create_child_profile', {
        p_display_name: args.displayName,
        p_member_color: args.memberColor,
        p_avatar_url: args.avatarUrl ?? null,
      });
      if (error) throw error;
      return data as { id: string; display_name: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-children'] });
      queryClient.invalidateQueries({ queryKey: ['family-members'] });
    },
  });
}

export function useUpdateChild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      childId: string;
      displayName?: string;
      memberColor?: string;
      avatarUrl?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('update_child_profile', {
        p_child_id: args.childId,
        p_display_name: args.displayName ?? null,
        p_member_color: args.memberColor ?? null,
        p_avatar_url: args.avatarUrl ?? null,
      });
      if (error) throw error;
      return data as { id: string; updated: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-children'] });
      queryClient.invalidateQueries({ queryKey: ['family-members'] });
      queryClient.invalidateQueries({ queryKey: ['family-leaderboard'] });
    },
  });
}

export function useAddGuardian() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { childId: string; guardianId: string }) => {
      const { data, error } = await supabase.rpc('add_guardian', {
        p_child_id: args.childId,
        p_guardian_id: args.guardianId,
      });
      if (error) throw error;
      return data as { child_id: string; guardian_id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-children'] });
    },
  });
}

export function useRemoveGuardian() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { childId: string; guardianId: string }) => {
      const { data, error } = await supabase.rpc('remove_guardian', {
        p_child_id: args.childId,
        p_guardian_id: args.guardianId,
      });
      if (error) throw error;
      return data as { child_id: string; guardian_id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-children'] });
    },
  });
}

export function useDeleteChild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (childId: string) => {
      const { data, error } = await supabase.rpc('delete_child_profile', {
        p_child_id: childId,
      });
      if (error) throw error;
      return data as { id: string; deleted: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-children'] });
      queryClient.invalidateQueries({ queryKey: ['family-members'] });
      queryClient.invalidateQueries({ queryKey: ['family-leaderboard'] });
    },
  });
}
